import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc' // import plugin
import { refreshJwt } from "./ApiHelper";

dayjs.extend(utc);

export interface IJwtAuthTokenResult {
    token?: string;
    expirationDate?: Date;
}

export class AuthHelper {
    private static signOutCallback: () => void;

    private static docubirdToken: IJwtAuthTokenResult;

    // Single-flight guard: concurrent requests hitting the interceptor near expiry share
    // one RefreshJwt call instead of each firing their own.
    private static refreshPromise: Promise<IJwtAuthTokenResult>;

    static setSignOutCallback(callback: () => void) {
        AuthHelper.signOutCallback = callback;
    }

    static signOut() {
        if (AuthHelper.signOutCallback) {
            AuthHelper.signOutCallback();
        }
    }
    static calculateMinutesToExpiration(tokenExpirationDate: Date): number {
        if (tokenExpirationDate) {
            try {
                const utcNow = dayjs.utc();
                const tokenExpirationUtc = dayjs.utc(tokenExpirationDate);
                const minutesToExpiration = tokenExpirationUtc.diff(utcNow, "minutes");
                return minutesToExpiration;
            } catch (error) {
                
            }
        }
        return 0;
    }

    static tokenRefreshRequired(tokenExpirationDate: Date): boolean {
        const minutesToExpiration = this.calculateMinutesToExpiration(tokenExpirationDate);
        if (minutesToExpiration > 2) {
            return false;
        }
        return true;
    }

    static async getDocubirdToken() {
        if (this.docubirdToken && this.tokenRefreshRequired(this.docubirdToken.expirationDate)) {
            if (!this.refreshPromise) {
                // Store the renewed token inside the shared chain, before the guard
                // clears in finally — a caller arriving in the microtask gap between the
                // two must see the fresh token rather than fire a second refresh.
                this.refreshPromise = refreshJwt(this.docubirdToken.token)
                    .then((token: IJwtAuthTokenResult) => {
                        if (token?.token) {
                            AuthHelper.docubirdToken = token;
                        }
                        return token;
                    })
                    .finally(() => { AuthHelper.refreshPromise = null; });
            }
            const refreshed: IJwtAuthTokenResult = await this.refreshPromise;
            // Expiry is checked at timestamp precision here, NOT via
            // calculateMinutesToExpiration: its minute-granularity diff truncates a
            // token's final <60s of validity to 0, misclassifying a still-usable
            // token as expired — and the refresh trigger above only fires inside the
            // 2-minute window, so that final minute is exactly when this fallback runs.
            const expirationDate = this.docubirdToken.expirationDate;
            if (!refreshed?.token && (!expirationDate || dayjs.utc(expirationDate).isBefore(dayjs.utc()))) {
                // Refresh failed and the token we hold is already expired (or carries no
                // expiry to judge by): every API call would 401 (and some callers
                // surface errors without signing out), so report no token — the axios
                // interceptor aborts the request and signs out. While the token is
                // still inside its validity window a failed refresh keeps the session
                // alive instead: the request proceeds with the current token and the
                // next request retries the refresh.
                return undefined;
            }
        }

        return this.docubirdToken?.token;
    }

    static setDocubirdToken(token: IJwtAuthTokenResult) {
        return this.docubirdToken = token;
    }


}